import React from 'react';
import Layout from '../../components/layout/Layout';
import Section from '../../components/layout/Section';
import CallToAction from '../../components/sections/CallToAction';
import TeamAvatar from '../../components/ui/TeamAvatar';
import { teamMembers, stats } from '../../data/mockData';

const AboutPage: React.FC = () => {
  return (
    <Layout>
      {/* Our Story Section */}
      <Section className="bg-neu-bg" padding="md">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center text-neu-text">Our Story</h2>
            <div className="prose prose-lg mx-auto text-neu-muted leading-relaxed">
              <p className="mb-6">
                Founded in 2025 by Praneet Srivastav, CodemyAI was born out of a vision to bridge
                the growing gap between the rapid evolution of artificial intelligence and its
                real-world application across businesses of all sizes. With years of experience
                delivering multi-million-dollar information technology projects, Praneet recognized
                that AI&apos;s transformative potential could only be fully realized if it became
                accessible—not just to tech giants with extensive R&amp;D resources, but to
                organizations and professionals everywhere.
              </p>
              <p className="mb-6">
                What began as a boutique consultancy assisting local businesses in adopting
                foundational AI solutions has since evolved into a full-service AI empowerment firm.
                Today, CodemyAI serves a diverse clientele ranging from individual professionals to
                enterprises of all sizes. Our guiding principle remains unchanged: AI should act as
                a force multiplier for human potential—not a substitute for it.
              </p>
              <p className="mb-6">
                Our approach blends deep technical expertise with a practical, results-driven
                methodology. We don&apos;t simply implement AI systems—we partner with our clients
                to build internal capabilities through education, coaching, and ongoing support.
                This client-first philosophy has fostered strong retention rates and organic growth
                driven by trusted referrals.
              </p>
              <p>
                As AI continues to reshape industries at an unprecedented pace, CodemyAI remains
                steadfast in its mission: to demystify cutting-edge AI technologies and empower
                organizations to harness them effectively, responsibly, and sustainably—delivering
                measurable business value every step of the way.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Company Stats Section */}
      <Section className="bg-neu-bg" padding="md">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-neu-text">Our Impact</h2>
          <div className="grid md:grid-cols-5 gap-6 text-center">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-neu-bg p-6 rounded-neu shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300"
              >
                <div className="text-4xl font-bold text-gradient-neu mb-2">{stat.value}</div>
                <p className="text-lg text-neu-text font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Our Mission and Values */}
      <Section className="bg-neu-bg" padding="md">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center text-neu-text">
              Our Mission &amp; Values
            </h2>

            <div className="mb-16">
              <h3 className="text-2xl font-semibold mb-4 text-center text-neu-text">Mission</h3>
              <p className="text-lg text-center mb-8 text-neu-muted">
                To democratize AI by making advanced technologies accessible, understandable, and
                valuable for businesses and individuals of all sizes and technical backgrounds.
              </p>
            </div>

            <h3 className="text-2xl font-semibold mb-8 text-center text-neu-text">
              Our Core Values
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-neu-bg p-6 rounded-neu shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300">
                <h4 className="text-xl font-semibold mb-3 text-neu-text">Practical Innovation</h4>
                <p className="text-neu-muted">
                  We bridge the gap between cutting-edge AI research and practical business
                  applications, focusing on solutions that deliver measurable value.
                </p>
              </div>
              <div className="bg-neu-bg p-6 rounded-neu shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300">
                <h4 className="text-xl font-semibold mb-3 text-neu-text">Empowering Education</h4>
                <p className="text-neu-muted">
                  We believe in teaching our clients to fish, not just giving them the fish.
                  Knowledge transfer and skill building are central to our approach.
                </p>
              </div>
              <div className="bg-neu-bg p-6 rounded-neu shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300">
                <h4 className="text-xl font-semibold mb-3 text-neu-text">Human-Centered AI</h4>
                <p className="text-neu-muted">
                  We develop AI solutions that enhance human capabilities and decision-making, not
                  replace them, with a focus on ethical implementation.
                </p>
              </div>
              <div className="bg-neu-bg p-6 rounded-neu shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300">
                <h4 className="text-xl font-semibold mb-3 text-neu-text">Measurable Results</h4>
                <p className="text-neu-muted">
                  We&apos;re committed to delivering and demonstrating concrete business outcomes
                  from every AI initiative we undertake.
                </p>
              </div>
              <div className="bg-neu-bg p-6 rounded-neu shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300">
                <h4 className="text-xl font-semibold mb-3 text-neu-text">Inclusive Excellence</h4>
                <p className="text-neu-muted">
                  We strive to make AI accessible across all industries, company sizes, and
                  technical backgrounds, with solutions tailored to each client&apos;s unique
                  context.
                </p>
              </div>
              <div className="bg-neu-bg p-6 rounded-neu shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300">
                <h4 className="text-xl font-semibold mb-3 text-neu-text">Continuous Learning</h4>
                <p className="text-neu-muted">
                  In a rapidly evolving field, we commit to ongoing education for ourselves and our
                  clients, constantly refining our approaches based on new developments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Team Section */}
      <Section className="bg-neu-bg" padding="md">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-neu-text">
            Our Leadership Team
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {teamMembers.map((member) => (
              <div
                key={member.id}
                className="bg-neu-bg rounded-neu shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300 overflow-hidden"
              >
                <div className="h-64 flex items-center justify-center">
                  {/* Professional Avatar based on role */}
                  <TeamAvatar name={member.name} title={member.title} />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-1 text-neu-text">{member.name}</h3>
                  <p className="text-neu-accent font-medium mb-3">{member.title}</p>
                  <p className="text-neu-muted mb-4">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Locations Section */}
      <Section className="bg-neu-bg" padding="md">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-neu-text">Our Locations</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-neu-bg p-8 rounded-neu shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300">
              <h3 className="text-xl font-semibold mb-4 text-neu-text">London Office</h3>
              <p className="mb-2 text-neu-muted">31 Giles Drive</p>
              <p className="mb-2 text-neu-muted">London</p>
              <p className="mb-4 text-neu-muted">United Kingdom</p>
              <p className="text-neu-accent font-medium">Main Office</p>
            </div>
            <div className="bg-neu-bg p-8 rounded-neu shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300">
              <h3 className="text-xl font-semibold mb-4 text-neu-text">India Office</h3>
              <p className="mb-2 text-neu-muted">19, Pride Complex</p>
              <p className="mb-2 text-neu-muted">Bangalore</p>
              <p className="mb-4 text-neu-muted">India</p>
              <p className="text-neu-accent font-medium">India Operations</p>
            </div>
            <div className="bg-neu-bg p-8 rounded-neu shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300">
              <h3 className="text-xl font-semibold mb-4 text-neu-text">Dublin Office</h3>
              <p className="mb-2 text-neu-muted">2, Stratten Walk</p>
              <p className="mb-2 text-neu-muted">Dublin</p>
              <p className="mb-4 text-neu-muted">Ireland</p>
              <p className="text-neu-accent font-medium">European Operations</p>
            </div>
          </div>
        </div>
      </Section>

      {/* Call to Action */}
      <CallToAction
        title="Ready to partner with us today?"
        description="Contact our team today to discuss how we can help you leverage AI to transform your business or career."
        ctaText="Get in Touch"
        ctaLink="/contact"
      />
    </Layout>
  );
};

export default AboutPage;
